import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  CheckCircle,
  XCircle,
  Clock,
  Calendar,
  User,
  Loader2,
  AlertCircle,
} from "lucide-react";
import { parseISO } from "date-fns";

const AprobacionesPage = () => {
  const [pendingRequests, setPendingRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [actionType, setActionType] = useState(null); // 'aprobar' | 'rechazar'
  const [comentario, setComentario] = useState("");
  const [processing, setProcessing] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  const fetchPendingRequests = async () => {
    try {
      const response = await api.getPendingRequests();
      setPendingRequests(response.data);
    } catch (error) {
      console.error("Error fetching pending requests:", error);
      toast.error("Error al cargar las solicitudes pendientes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPendingRequests();
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      // Usar parseISO para evitar el desfase por zona horaria UTC
      return parseISO(dateString).toLocaleDateString("es-GT", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
    } catch (e) {
      return dateString;
    }
  };

  const handleOpenAction = (request, action) => {
    setSelectedRequest(request);
    setActionType(action);
    setComentario("");
    setDialogOpen(true);
  };

  const handleAction = async () => {
    if (!selectedRequest || !actionType) return;

    setProcessing(true);

    try {
      if (actionType === "aprobar") {
        await api.approveRequest(selectedRequest.id, comentario);
        toast.success("Solicitud aprobada", {
          description: `Las vacaciones de ${selectedRequest.empleado_nombre} han sido aprobadas`,
        });
      } else {
        await api.rejectRequest(selectedRequest.id, comentario);
        toast.success("Solicitud rechazada", {
          description: `Las vacaciones de ${selectedRequest.empleado_nombre} han sido rechazadas`,
        });
      }

      setDialogOpen(false);
      fetchPendingRequests();
    } catch (error) {
      console.error("Error processing request:", error);
      toast.error("Error al procesar la solicitud");
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="aprobaciones-page">
      <div>
        <h1 className="text-3xl font-bold text-slate-800 tracking-tight">
          Aprobación de Solicitudes
        </h1>
        <p className="text-slate-500 mt-1">
          Revisa y gestiona las solicitudes de vacaciones de tu equipo
        </p>
      </div>

      {/* Pending Count */}
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2 px-4 py-2 bg-amber-50 rounded-lg border border-amber-200">
          <Clock className="w-5 h-5 text-amber-600" />
          <span className="text-amber-800 font-medium">
            {pendingRequests.length} solicitudes pendientes
          </span>
        </div>
      </div>

      {/* Requests Table */}
      <Card className="border-slate-100">
        <CardContent className="p-0">
          {pendingRequests.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>Colaborador</TableHead>
                  <TableHead>Período</TableHead>
                  <TableHead>Días</TableHead>
                  <TableHead>Motivo</TableHead>
                  <TableHead>Fecha Solicitud</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pendingRequests.map((request) => (
                  <TableRow key={request.id} className="hover:bg-slate-50">
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
                          <User className="w-4 h-4 text-orange-600" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-800">
                            {request.empleado_nombre}
                          </p>
                          <p className="text-xs text-slate-500">
                            {request.empleado_email}
                          </p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-sky-500" />
                        <span className="text-slate-800">
                          {formatDate(request.fecha_inicio)} - {formatDate(request.fecha_fin)}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="bg-sky-50 text-sky-700 border-sky-200">
                        {request.dias_solicitados} días
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <span className="text-slate-600 truncate max-w-[200px] block">
                        {request.motivo}
                      </span>
                    </TableCell>
                    <TableCell className="text-slate-500">
                      {formatDate(request.fecha_creacion)}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          size="sm"
                          className="bg-emerald-500 hover:bg-emerald-600 text-white"
                          onClick={() => handleOpenAction(request, "aprobar")}
                          data-testid={`approve-btn-${request.id}`}
                        >
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Aprobar
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-red-200 text-red-600 hover:bg-red-50"
                          onClick={() => handleOpenAction(request, "rechazar")}
                          data-testid={`reject-btn-${request.id}`}
                        >
                          <XCircle className="w-4 h-4 mr-1" />
                          Rechazar
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-12">
              <CheckCircle className="w-12 h-12 text-emerald-300 mx-auto mb-3" />
              <p className="text-slate-500 font-medium">
                No hay solicitudes pendientes
              </p>
              <p className="text-slate-400 text-sm mt-1">
                Todas las solicitudes han sido procesadas
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Action Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {actionType === "aprobar" ? (
                <>
                  <CheckCircle className="w-5 h-5 text-emerald-500" />
                  Aprobar Solicitud
                </>
              ) : (
                <>
                  <XCircle className="w-5 h-5 text-red-500" />
                  Rechazar Solicitud
                </>
              )}
            </DialogTitle>
            <DialogDescription>
              {actionType === "aprobar" 
                ? "Confirma la aprobación de las vacaciones solicitadas" 
                : "Confirma el rechazo de las vacaciones solicitadas"}
            </DialogDescription>
          </DialogHeader>

          {selectedRequest && (
            <div className="space-y-4">
              {/* Request Summary */}
              <div className="p-4 bg-slate-50 rounded-lg space-y-2">
                <p className="font-medium text-slate-800">
                  {selectedRequest.empleado_nombre}
                </p>
                <div className="flex items-center gap-4 text-sm text-slate-600">
                  <span className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    {formatDate(selectedRequest.fecha_inicio)} - {formatDate(selectedRequest.fecha_fin)}
                  </span>
                  <Badge variant="outline">{selectedRequest.dias_solicitados} días</Badge>
                </div>
                <p className="text-sm text-slate-500">{selectedRequest.motivo}</p>
              </div>

              {/* Comment Field */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">
                  Comentario {actionType === "rechazar" ? "(recomendado)" : "(opcional)"}
                </label>
                <Textarea
                  placeholder={
                    actionType === "aprobar"
                      ? "Agregar un comentario..."
                      : "Explica el motivo del rechazo..."
                  }
                  value={comentario}
                  onChange={(e) => setComentario(e.target.value)}
                  rows={3}
                  data-testid="comentario-input"
                />
              </div>

              {actionType === "rechazar" && !comentario && (
                <div className="flex items-center gap-2 text-amber-600 text-sm">
                  <AlertCircle className="w-4 h-4" />
                  Se recomienda agregar un motivo del rechazo
                </div>
              )}
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setDialogOpen(false)}
              disabled={processing}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleAction}
              disabled={processing}
              className={
                actionType === "aprobar"
                  ? "bg-emerald-500 hover:bg-emerald-600 text-white"
                  : "bg-red-500 hover:bg-red-600 text-white"
              }
              data-testid="confirm-action-btn"
            >
              {processing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Procesando...
                </>
              ) : actionType === "aprobar" ? (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Confirmar Aprobación
                </>
              ) : (
                <>
                  <XCircle className="w-4 h-4 mr-2" />
                  Confirmar Rechazo
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AprobacionesPage;
